import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type SuiteChipData = { icon: string; label: string };

export type SuiteRecord = {
  id: string;
  slug: string;
  name: string;
  tagline: string;
  short_description: string;
  long_description: string;
  occupancy: string;
  bed: string;
  chips: SuiteChipData[];
  sort_order: number;
};

export type SuitePhotoRecord = {
  id: string;
  suite_id: string;
  url: string;
  storage_path: string | null;
  alt: string;
  sort_order: number;
};

export type SuiteWithPhotos = SuiteRecord & { photos: SuitePhotoRecord[] };

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

export const listSuitesPublic = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    if (!i) return {} as { experience_slug?: string };
    const o = i as { experience_slug?: string };
    return { experience_slug: o.experience_slug };
  })
  .handler(async ({ data }) => {
  const sb = publicClient();
  const expSlug = data?.experience_slug ?? null;
  const { data: suites, error } = await sb
    .from("suites")
    .select("*")
    .order("sort_order", { ascending: true });
  if (error) throw new Error(error.message);
  const ids = (suites ?? []).map((s) => s.id);
  let photos: SuitePhotoRecord[] = [];
  if (ids.length) {
    const { data: ph, error: e2 } = await sb
      .from("suite_photos")
      .select("id, suite_id, url, alt, sort_order")
      .in("suite_id", ids)
      .order("sort_order", { ascending: true });
    if (e2) throw new Error(e2.message);
    photos = (ph ?? []).map((p) => ({ ...p, storage_path: null })) as SuitePhotoRecord[];

  }

  // Resolve overrides + photo visibility for selected experience
  let expId: string | null = null;
  const overrideByKey = new Map<string, { text: string | null; url: string | null }>();
  const photoAllow = new Map<string, Set<string>>();
  if (expSlug) {
    const { data: exp } = await sb.from("experiences").select("id").eq("slug", expSlug).maybeSingle();
    expId = exp?.id ?? null;
    if (expId) {
      const [ov, px] = await Promise.all([
        sb
          .from("page_overrides")
          .select("field_key,experience_id,value_text,value_url")
          .eq("page_key", "suites")
          .eq("experience_id", expId),
        sb
          .from("gallery_image_experiences")
          .select("image_id,experience_id")
          .eq("image_table", "suite_photos"),
      ]);
      for (const o of ov.data ?? []) {
        overrideByKey.set(o.field_key, { text: o.value_text, url: o.value_url });
      }
      for (const l of px.data ?? []) {
        const s = photoAllow.get(l.image_id) ?? new Set<string>();
        s.add(l.experience_id);
        photoAllow.set(l.image_id, s);
      }
    }
  }

  const pickText = (key: string, fallback: string) => {
    const v = overrideByKey.get(key);
    return v?.text ?? fallback;
  };

  return (suites ?? []).map((s) => {
    const raw = s as unknown as Record<string, unknown>;
    const slug = raw.slug as string;
    const suitePhotos = photos.filter((p) => p.suite_id === (raw.id as string)).filter((p) => {
      if (!expId) return true;
      const allow = photoAllow.get(p.id);
      if (!allow) return true;
      return allow.has(expId);
    });
    const base = raw as unknown as SuiteRecord;
    return {
      ...base,
      short_description: pickText(`suite.${slug}.short_description`, base.short_description),
      long_description: pickText(`suite.${slug}.long_description`, base.long_description),
      tagline: pickText(`suite.${slug}.tagline`, base.tagline),
      chips: Array.isArray(raw.chips) ? (raw.chips as SuiteChipData[]) : [],
      photos: suitePhotos,
    };
  }) as SuiteWithPhotos[];
});

export const upsertSuite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: {
    id?: string;
    slug: string;
    name: string;
    tagline: string;
    short_description: string;
    long_description: string;
    occupancy: string;
    bed: string;
    chips: SuiteChipData[];
  }) => d)
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    if (data.id) {
      const { error } = await supabase
        .from("suites")
        .update({
          slug: data.slug,
          name: data.name,
          tagline: data.tagline,
          short_description: data.short_description,
          long_description: data.long_description,
          occupancy: data.occupancy,
          bed: data.bed,
          chips: data.chips,
        })
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: rows, error } = await supabase
      .from("suites")
      .select("sort_order")
      .order("sort_order", { ascending: false })
      .limit(1);
    if (error) throw new Error(error.message);
    const next = (rows?.[0]?.sort_order ?? -1) + 1;
    const { data: inserted, error: e2 } = await supabase
      .from("suites")
      .insert({
        slug: data.slug,
        name: data.name,
        tagline: data.tagline,
        short_description: data.short_description,
        long_description: data.long_description,
        occupancy: data.occupancy,
        bed: data.bed,
        chips: data.chips,
        sort_order: next,
      })
      .select("id")
      .single();
    if (e2) throw new Error(e2.message);
    return { id: inserted.id };
  });

export const deleteSuite = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.from("suites").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderSuites = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { ordered_ids: string[] }) => d)
  .handler(async ({ data, context }) => {
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await context.supabase
        .from("suites")
        .update({ sort_order: i })
        .eq("id", data.ordered_ids[i]);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const addSuitePhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { suite_id: string; url: string; storage_path?: string; alt?: string }) => d)
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: rows, error } = await supabase
      .from("suite_photos")
      .select("sort_order")
      .eq("suite_id", data.suite_id)
      .order("sort_order", { ascending: false })
      .limit(1);
    if (error) throw new Error(error.message);
    const next = (rows?.[0]?.sort_order ?? -1) + 1;
    const { data: inserted, error: e2 } = await supabase
      .from("suite_photos")
      .insert({
        suite_id: data.suite_id,
        url: data.url,
        storage_path: data.storage_path ?? null,
        alt: data.alt ?? "",
        sort_order: next,
      })
      .select("id")
      .single();
    if (e2) throw new Error(e2.message);
    return { id: inserted.id };
  });

export const deleteSuitePhoto = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => d)
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    const { data: row } = await supabase
      .from("suite_photos")
      .select("storage_path")
      .eq("id", data.id)
      .maybeSingle();
    if (row?.storage_path) {
      await supabase.storage.from("slide-media").remove([row.storage_path]);
    }
    const { error } = await supabase.from("suite_photos").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const reorderSuitePhotos = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { suite_id: string; ordered_ids: string[] }) => d)
  .handler(async ({ data, context }) => {
    for (let i = 0; i < data.ordered_ids.length; i++) {
      const { error } = await context.supabase
        .from("suite_photos")
        .update({ sort_order: i })
        .eq("id", data.ordered_ids[i])
        .eq("suite_id", data.suite_id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

export const updateSuitePhotoUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: { id: string; url: string; storage_path?: string | null }) => d,
  )
  .handler(async ({ data, context }) => {
    const { supabase } = context;
    // Capture the previous storage_path so we can clean up after replacing it.
    const { data: prev } = await supabase
      .from("suite_photos")
      .select("storage_path")
      .eq("id", data.id)
      .maybeSingle();
    const { error } = await supabase
      .from("suite_photos")
      .update({ url: data.url, storage_path: data.storage_path ?? null })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    if (prev?.storage_path && prev.storage_path !== data.storage_path) {
      await supabase.storage.from("slide-media").remove([prev.storage_path]);
    }
    return { ok: true };
  });

// ---------- Suites page content (hero + amenities) ----------

export type SuitesContent = {
  id: string;
  hero_image_url: string | null;
  hero_kicker: string | null;
  hero_title: string | null;
  hero_subtitle: string | null;
  amenities_kicker: string | null;
  amenities_image_url: string | null;
  amenities: string[];
};

const SUITES_CONTENT_COLS =
  "id,hero_image_url,hero_kicker,hero_title,hero_subtitle,amenities_kicker,amenities_image_url,amenities";

export const getSuitesPageContent = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => {
    if (!i) return {} as { experience_slug?: string };
    const o = i as { experience_slug?: string };
    return { experience_slug: o.experience_slug };
  })
  .handler(async ({ data }): Promise<SuitesContent | null> => {
    const sb = publicClient();
    const expSlug = data?.experience_slug ?? null;
    const [c, ov, exp] = await Promise.all([
      sb
        .from("suites_content")
        .select(SUITES_CONTENT_COLS)
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle(),
      sb
        .from("page_overrides")
        .select("field_key,experience_id,value_text,value_url")
        .eq("page_key", "suites"),
      expSlug
        ? sb.from("experiences").select("id").eq("slug", expSlug).maybeSingle()
        : Promise.resolve({ data: null, error: null } as const),
    ]);
    if (c.error) throw new Error(c.error.message);
    if (ov.error) throw new Error(ov.error.message);
    if ("error" in exp && exp.error) throw new Error(exp.error.message);
    if (!c.data) return null;
    const raw = c.data as unknown as Record<string, unknown>;
    let content: SuitesContent = {
      id: raw.id as string,
      hero_image_url: (raw.hero_image_url as string) ?? null,
      hero_kicker: (raw.hero_kicker as string) ?? null,
      hero_title: (raw.hero_title as string) ?? null,
      hero_subtitle: (raw.hero_subtitle as string) ?? null,
      amenities_kicker: (raw.amenities_kicker as string) ?? null,
      amenities_image_url: (raw.amenities_image_url as string) ?? null,
      amenities: Array.isArray(raw.amenities) ? (raw.amenities as string[]) : [],
    };
    const expId =
      ("data" in exp && exp.data ? (exp.data as { id: string }).id : null) ||
      null;
    if (expId) {
      const byField = new Map<string, { text: string | null; url: string | null }>();
      for (const o of ov.data ?? []) {
        if (o.experience_id === expId)
          byField.set(o.field_key, { text: o.value_text, url: o.value_url });
      }
      const pick = (key: string, fallback: string | null) => {
        const v = byField.get(key);
        if (!v) return fallback;
        return v.text ?? v.url ?? fallback;
      };
      content = {
        ...content,
        hero_image_url: pick("page.hero_image", content.hero_image_url),
        hero_kicker: pick("page.hero_kicker", content.hero_kicker),
        hero_title: pick("page.hero_title", content.hero_title),
        hero_subtitle: pick("page.hero_subtitle", content.hero_subtitle),
        amenities_kicker: pick("page.amenities_kicker", content.amenities_kicker),
        amenities_image_url: pick("page.amenities_image", content.amenities_image_url),
        amenities: (() => {
          const v = byField.get("page.amenities");
          if (!v?.text) return content.amenities;
          return v.text
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean);
        })(),
      };
    }
    return content;
  });

export const upsertSuitesContent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (d: {
      id?: string;
      hero_image_url: string | null;
      hero_kicker: string | null;
      hero_title: string | null;
      hero_subtitle: string | null;
      amenities_kicker: string | null;
      amenities_image_url: string | null;
      amenities: string[];
    }) => d,
  )
  .handler(async ({ data, context }) => {
    const sb = context.supabase;
    const payload = {
      hero_image_url: data.hero_image_url,
      hero_kicker: data.hero_kicker,
      hero_title: data.hero_title,
      hero_subtitle: data.hero_subtitle,
      amenities_kicker: data.amenities_kicker,
      amenities_image_url: data.amenities_image_url,
      amenities: data.amenities,
    };
    if (data.id) {
      const { error } = await sb.from("suites_content").update(payload).eq("id", data.id);
      if (error) throw new Error(error.message);
      return { id: data.id };
    }
    const { data: row, error } = await sb
      .from("suites_content")
      .insert(payload)
      .select("id")
      .single();
    if (error) throw new Error(error.message);
    return { id: row.id as string };
  });


